import { useCallback } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import {
  CheckCircleFill,
  Clock,
  ClockNoFill,
  CoinsFill,
  CoinsHand,
  CoinsStacked,
  LockFill,
  PauseCircleFill,
  PlayCircle,
  PlayCircleFill,
  StopCircle,
  StopCircleFill,
  User,
} from "@hl/base-components/lib/assets/icons.generated";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import useAnalytics from "@hl/shared-features/lib/features/analytics/useAnalytics";
import ScheduledPricesList from "@hl/shared-features/lib/features/mint/ScheduledPricesList";
import {
  PushableModalProps,
  useModalStack,
} from "@hl/shared-features/lib/features/modal";
import { maskAddress } from "@hl/shared-features/lib/utils/content";
import { getCurrencySymbol } from "@hl/shared-features/lib/utils/currency";
import { isUnlimitedSize } from "@hl/shared-features/lib/utils/unlimitedSize";
import { CardProps, Divider, Group, Paper, Stack, Title } from "@mantine/core";

import {
  _CollectionType,
  MintVectorStats,
  MintVectorStatus,
  PriceType,
} from "apollo/graphql.generated";
import { ReactComponent as EyeOff } from "assets/icons/eye-off.svg";
import { ReactComponent as DirectMintIcon } from "assets/icons/grid-circle.svg";
import { ReactComponent as Star6 } from "assets/icons/star-06.svg";
import { getMintPageUrl } from "config";
import { longFormatDate } from "utils/date";
import SaleButtonGroup from "~/features/manage-collection/mint-vector/SaleButtonGroup";
import { GetManageCollectionQuery } from "~/features/manage-collection/v2.queries.graphql.generated";
import { SaleData } from "~/features/tokens/sections/ManageToken/MintVector/SaleData";
import SaleStatusBox, {
  SaleConfig,
} from "~/features/tokens/sections/ManageToken/SaleStatusBox";
import EditSaleModal from "~/features/tokens/sections/ManageToken/modals/EditSaleModal";
import { PropertyCard } from "~/features/tokens/sections/ManageToken/properties/PropertyCard";
import { useGetManageTokenGateQuery } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";
import { SaleStatus } from "~/features/tokens/sections/ManageToken/types";

export const ManageMintVector = ({
  collection,
  mintVector,
  ...rest
}: {
  collection: GetManageCollectionQuery["getManageCollection"];
  mintVector: GetManageCollectionQuery["getManageCollection"]["mintVectors"][0];
} & Omit<CardProps, "children">) => {
  const { pushModal, popModal } = useModalStack();
  const { trackEvent, EventType } = useAnalytics();
  const isDeploying = collection.status === "IN_PROGRESS";
  const edition = collection?.editions?.length
    ? collection?.editions[0]
    : {
        name: collection?.name || "",
        description: "",
        image: "",
        size: 100,
        onChainImage: "",
        onChainMetadata: "",
        royalty: null,
        nonTransferable: null,
      };

  const mintUrl = getMintPageUrl(collection);

  const inProgressStats: MintVectorStats = {
    total: 0,
    sold: 0,
    status: MintVectorStatus.NotStarted,
    earned: "0",
  };
  const stats = mintVector?.stats ?? inProgressStats;
  const saleStatus: SaleStatus = convertToSaleStatus(
    !!mintVector.paused,
    stats.total,
    stats.sold,
    stats.status
  );
  const displayMintVector = mintVector && {
    id: mintVector.id || "",
    startDate: mintVector.start,
    endDate: mintVector.end,
    price: mintVector.price,
    chainId: mintVector.chainId?.toString() || "",
    maxPerUser: mintVector.maxPerUser,
    collectionId: collection.id || "",
    gateId: mintVector.gateId,
    paymentRecipient: mintVector.paymentRecipient,
    nonTransferable: edition?.nonTransferable,
    priceType: mintVector?.priceType,
    priceDropInterval: mintVector?.priceDropInterval,
    scheduledPrices: mintVector?.scheduledPrices,
    priceDrops: mintVector?.priceDrops,
    isDirectMint: mintVector?.isDirectMint ?? false,
  };

  const editSaleOnClick = useCallback(() => {
    const nestedModalProps: PushableModalProps = {
      size: "xl",
    };
    pushModal(
      <EditSaleModal
        mintVectorId={displayMintVector.id}
        startDate={new Date(displayMintVector.startDate)}
        endDate={
          displayMintVector.endDate
            ? new Date(displayMintVector.endDate)
            : undefined
        }
        price={displayMintVector.price}
        chainId={displayMintVector.chainId}
        maxPerUser={displayMintVector.maxPerUser}
        nonTransferable={displayMintVector.nonTransferable || false}
        paymentRecipient={displayMintVector.paymentRecipient}
        paymentRecipientEOA={displayMintVector.paymentRecipient}
        gateId={mintVector.gateId || ""}
        paused={mintVector.paused || false}
        onClose={popModal}
        priceDropInterval={displayMintVector.priceDropInterval}
        priceType={displayMintVector.priceType}
        scheduledPrices={displayMintVector.scheduledPrices}
        collectionType={collection.collectionType}
        collectionId={collection.id}
        isDirectMint={mintVector.isDirectMint ?? false}
      />,
      {
        ...nestedModalProps,
        title: (
          <Group spacing="xs">
            <Title size={18}>Edit Mint</Title>
          </Group>
        ),
      }
    );
    trackEvent(EventType.EditToken_EditSaleModalOpened, {
      collectionId: displayMintVector.collectionId,
      mintVectorId: mintVector.id,
    });
  }, [mintVector]);

  const minted = useCallback(() => {
    if (isDeploying) return "-";
    if (collection.collectionType === _CollectionType.OneOfOne) {
      return stats.sold > 0 ? "Yes" : "No";
    }
    return `${stats.sold}${
      isUnlimitedSize(stats.total) ? "" : "/" + stats.total
    }`;
  }, [isDeploying, stats, edition?.size, collection.collectionType]);

  const { data: gateData, loading: gateDataLoading } =
    useGetManageTokenGateQuery({
      variables: {
        id: mintVector.gateId || "",
      },
      skip: isDeploying || !mintVector.gateId,
    });

  if (gateDataLoading) return <LoadingPage />;

  const endDate = mintVector.end ? new Date(mintVector.end) : undefined;
  const saleConfig = saleConfigMap[saleStatus];
  const isFixedPrice =
    !mintVector.priceType || mintVector.priceType === PriceType.Fixed;
  return (
    <Paper withBorder={false} shadow="sm" {...rest}>
      <SaleStatusBox
        onEditSaleClick={editSaleOnClick}
        editButton="Edit mint"
        showEditButton={true}
        saleConfig={saleConfig}
        startDate={new Date(mintVector.start)}
      />
      <Divider />

      <Stack p={16}>
        <Group grow spacing="xl" align="baseline">
          <SaleData
            label="Primary Sales"
            value={mintVector.stats?.earned}
            suffix={getCurrencySymbol(mintVector.chainId || "")}
          />
          <SaleData label="Minted" value={minted()} />
        </Group>
        {isFixedPrice ? (
          <PropertyCard
            icon={<CoinsFill width={20} height={20} />}
            label="Price"
            value={`${mintVector.price} ${getCurrencySymbol(
              mintVector.chainId || ""
            )}`}
          />
        ) : (
          <>
            <PropertyCard
              icon={<CoinsFill width={20} height={20} />}
              label="Price"
              value={<ScheduledPricesList priceDrops={mintVector.priceDrops} />}
            />
            <PropertyCard
              icon={<ClockNoFill width={20} height={20} />}
              label="Price drop interval"
              value={mintVector.priceDropInterval?.map((period) =>
                period.value > 0
                  ? ` ${period.value}${period.unit.charAt(0).toLowerCase()}`
                  : ""
              )}
            />
          </>
        )}
        {saleStatus === SaleStatus.NOT_STARTED && (
          <PropertyCard
            icon={<PlayCircle width={20} height={20} />}
            label="Start date"
            value={longFormatDate(new Date(mintVector.start))}
          />
        )}
        <PropertyCard
          icon={<StopCircle width={20} height={20} />}
          label="End date"
          value={endDate ? longFormatDate(endDate) : "None"}
        />
        {collection.collectionType === _CollectionType.Series ? (
          <>
            <PropertyCard
              icon={<Star6 width={20} height={20} />}
              label="Mint style"
              value={collection.reveal ? "Random mint" : "Collector's choice"}
            />
            <PropertyCard
              icon={<EyeOff width={20} height={20} />}
              label="Hide series metadata"
              value={
                collection.seriesDetails?.showAllMetadata === false
                  ? "Yes"
                  : "No"
              }
            />
          </>
        ) : null}
        {!mintVector.isDirectMint && (
          <PropertyCard
            icon={<LockFill width={20} height={20} />}
            label="Gate"
            value={
              gateData && gateData.gate.id ? gateData.gate.name : "No gate"
            }
          />
        )}
        {mintVector.isDirectMint && (
          <PropertyCard
            icon={<DirectMintIcon width={20} height={20} />}
            label="Enable direct minting"
            value={"Yes"}
          />
        )}

        <PropertyCard
          icon={<CoinsHand width={20} height={20} />}
          label="Limit mints per wallet"
          value={mintVector.maxPerUser !== 0 ? "Yes" : "No"}
        />
        {mintVector.maxPerUser !== 0 && (
          <PropertyCard
            icon={<CoinsStacked width={20} height={20} />}
            label="Maximum mints allowed per wallet"
            value={mintVector.maxPerUser.toString()}
          />
        )}
        {mintVector?.paymentRecipient && (
          <PropertyCard
            icon={<User width={20} height={20} />}
            label="Payout address"
            value={maskAddress(mintVector.paymentRecipient, 6, 4)}
          />
        )}
      </Stack>

      <Divider />
      <SaleButtonGroup
        status={saleStatus}
        mintVector={displayMintVector}
        collection={collection}
        mintUrl={mintUrl}
      />
    </Paper>
  );
};

const convertToSaleStatus = (
  paused: boolean,
  total: number,
  sold: number,
  status: MintVectorStatus
) => {
  if (total && sold >= total) {
    return SaleStatus.SOLD_OUT;
  }

  if (paused) {
    return SaleStatus.PAUSED;
  }

  switch (status) {
    case MintVectorStatus.Ended:
      return SaleStatus.ENDED;
    case MintVectorStatus.InProgress:
      return SaleStatus.ACTIVE;
    case MintVectorStatus.NotStarted:
      return SaleStatus.NOT_STARTED;
  }
};

const saleConfigMap: { [key in SaleStatus]: SaleConfig } = {
  [SaleStatus.NOT_STARTED]: {
    mintStatus: "Mint starts in :days",
    hasStarted: true,
    color: TEXT_COLOR.PRIMARY,
    icon: <Clock width={20} height={20} />,
  },
  [SaleStatus.ACTIVE]: {
    mintStatus: "Mint active",
    color: STATUS_COLOR.AFFIRMATIVE,
    icon: <PlayCircleFill width={20} height={20} />,
  },
  [SaleStatus.ENDED]: {
    mintStatus: "Mint ended",
    color: TEXT_COLOR.SECONDARY,
    icon: <StopCircleFill width={20} height={20} />,
  },
  [SaleStatus.PAUSED]: {
    mintStatus: "Mint paused",
    color: TEXT_COLOR.SECONDARY,
    icon: <PauseCircleFill width={20} height={20} />,
  },
  [SaleStatus.SOLD_OUT]: {
    mintStatus: "Sold out",
    color: TEXT_COLOR.SECONDARY,
    icon: <CheckCircleFill width={20} height={20} />,
  },
  [SaleStatus.NEEDS_FULFILLMENT]: {
    mintStatus: "Mint ended",
    color: TEXT_COLOR.SECONDARY,
    icon: <CheckCircleFill width={20} height={20} />,
  },
  [SaleStatus.RESERVE_NOT_MET]: {
    mintStatus: "Mint ended",
    color: TEXT_COLOR.SECONDARY,
    icon: <CheckCircleFill width={20} height={20} />,
  },
};
